import { cva } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../../styles/lib/utils';

// Parked above the viewport instead of `sr-only`: the app's Font Awesome stylesheet ships an unlayered `.sr-only` that outranks `focus:not-sr-only`.
const skipLinkVariants = cva(
  'fixed top-2 left-2 z-[9999] -translate-y-[calc(100%+1rem)] rounded bg-blue-50 px-4 py-2 text-sm font-semibold text-white focus:translate-y-0 focus-ring motion-safe:transition-transform',
);

type SkipLinkProps = Omit<React.ComponentProps<'a'>, 'href'> & {
  /** Id of the element that receives focus; it needs `tabIndex={-1}` unless natively focusable. */
  targetId: string;
};

/** Moves focus to the target rather than only updating the URL hash, so the next Tab continues from inside it. */
function focusSkipTarget(event: React.MouseEvent<HTMLAnchorElement>, targetId: string) {
  const target = document.getElementById(targetId);
  if (!target) return;
  event.preventDefault();
  target.focus();
}

function SkipLink({ targetId, className, onClick, children = 'Skip to main content', ...props }: SkipLinkProps) {
  return (
    <a
      href={`#${targetId}`}
      data-slot="skip-link"
      className={cn(skipLinkVariants(), className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) focusSkipTarget(event, targetId);
      }}
      {...props}
    >
      {children}
    </a>
  );
}

export { focusSkipTarget, SkipLink, skipLinkVariants };
export type { SkipLinkProps };
